import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type {
	ChartStageBar,
	ConstructionProgressResponse,
} from "@/adapters/api";
import { Button } from "@/components/ui/button.tsx";
import { useConstructionProgress } from "../../hooks/use-construction-progress.ts";
import type { StageDrillLevel } from "../../types.ts";
import { formatPercent, formatPrecisePercent } from "../../utils/format.ts";
import { Chart } from "../chart/index.tsx";

export interface StageBarsProps {
	constructionId: string | undefined;
	referenceDate: string;
	rootData?: ConstructionProgressResponse;
	isPending: boolean;
	isError: boolean;
}

export function StageBars({
	constructionId,
	referenceDate,
	rootData,
	isPending,
	isError,
}: StageBarsProps) {
	const [path, setPath] = useState<StageDrillLevel[]>([]);
	const current = path.at(-1);

	const drill = useConstructionProgress({
		constructionId,
		referenceDate,
		stageId: current?.stageId,
		enabled: !!current,
	});

	const data = current ? drill.data : rootData;
	const pending = current ? drill.isPending : isPending;
	const failed = current ? drill.isError : isError;
	const stages: ChartStageBar[] = data?.stages ?? [];
	const drillable = stages.filter((stage) => stage.has_children);

	function enter(stage: ChartStageBar) {
		if (!stage.has_children) return;
		setPath((levels) => [
			...levels,
			{ stageId: stage.stage_id, name: stage.name },
		]);
	}

	function enterFromBar(entry: unknown) {
		const stage = (entry as { payload?: ChartStageBar }).payload;
		if (stage) enter(stage);
	}

	return (
		<div className="stage-bars">
			<div className="stage-bars__nav">
				{current && (
					<Button
						variant="ghost"
						size="sm"
						className="stage-bars__back"
						onClick={() => setPath((levels) => levels.slice(0, -1))}
					>
						<ChevronLeft />
						Voltar
					</Button>
				)}
				<p className="stage-bars__level">
					{["Obra", ...path.map((level) => level.name)].join(" › ")}
				</p>
			</div>

			<Chart
				label={`Avanço planejado e realizado por etapa em ${current?.name ?? "toda a obra"}`}
				height={260}
				isPending={pending}
				isError={failed}
				isEmpty={stages.length === 0}
				emptyMessage="Nenhuma etapa neste nível."
				table={{
					columns: ["Etapa", "Planejado", "Realizado"],
					rows: stages.map((stage) => [
						stage.name,
						formatPrecisePercent(stage.planned_percent),
						formatPrecisePercent(stage.actual_percent),
					]),
				}}
			>
				<BarChart
					data={stages}
					margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
				>
					<CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
					<XAxis dataKey="name" tickLine={false} axisLine={false} />
					<YAxis
						tickFormatter={(value) => formatPercent(Number(value))}
						tickLine={false}
						axisLine={false}
						domain={[0, 100]}
					/>
					<Tooltip formatter={(value) => formatPrecisePercent(Number(value))} />
					<Legend />
					<Bar
						dataKey="planned_percent"
						name="Planejado"
						fill="var(--chart-3)"
						radius={[4, 4, 0, 0]}
						onClick={enterFromBar}
					/>
					<Bar
						dataKey="actual_percent"
						name="Realizado"
						fill="var(--chart-1)"
						radius={[4, 4, 0, 0]}
						onClick={enterFromBar}
					/>
				</BarChart>
			</Chart>

			{drillable.length > 0 && (
				<div className="stage-bars__drill">
					<p className="stage-bars__drill-label">Detalhar etapa</p>
					<ul className="stage-bars__drill-list">
						{drillable.map((stage) => (
							<li key={stage.stage_id}>
								<Button
									variant="outline"
									size="sm"
									onClick={() => enter(stage)}
								>
									{stage.name}
								</Button>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}
